import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../auth.service';
import { TenantBrandingService } from '../../shared/services/tenant-branding/tenant-branding.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { LanguageSelectorComponent } from '../../shared/components/language-selector/language-selector.component';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatButtonModule,
    MatProgressBar,
    TranslateModule,
    LanguageSelectorComponent,
    RouterLink,
  ],
})
export class LoginComponent implements OnInit {
  public tenantBrandingService = inject(TenantBrandingService);
  private fb = inject(UntypedFormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private aroute = inject(ActivatedRoute);

  onLogoError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/creatives/logo-w70.png';
  }

  resetPasswordMode = false;
  loginForm: UntypedFormGroup;
  forgotPasswordForm: UntypedFormGroup;
  resetPasswordForm: UntypedFormGroup;
  newPassword = '';
  newPasswordConf = '';
  forgottenPasswordToken;
  isForgotPasswordView = false;
  passwordsNeedsRenewal = false;
  loading = false;
  hide = true;
  hideNewPassword = true;
  hideConfirmPassword = true;
  
  passwordStrength = {
    score: 0,
    level: 'weak',
    label: 'Sessions.Login.PasswordStrength.Weak'
  };

  passwordCriteria = [
    { key: 'length', label: 'Sessions.Login.PasswordCriteria.Length', met: false },
    { key: 'uppercase', label: 'Sessions.Login.PasswordCriteria.Uppercase', met: false },
    { key: 'number', label: 'Sessions.Login.PasswordCriteria.Number', met: false },
    { key: 'special', label: 'Sessions.Login.PasswordCriteria.Special', met: false }
  ];

  passwordsMatch = true;

  selectedLanguage = localStorage.getItem('language') || 'fr';
  showLoginPoster = environment.showLoginPoster ?? false;
  inviteInfo: any = null;

  constructor() {
    this.translate.onLangChange.subscribe((params) => {
      this.selectedLanguage = params.lang;
    });
  }

  ngOnInit() {
    // Auto-login via token (e.g., from onboarding flow)
    setTimeout(async () => {
      const loginParam = this.aroute.snapshot.queryParamMap.get('login');
      if (loginParam) {
        this.loading = true;
        try {
          await this.authService.loginWithToken(loginParam, true);
        } catch (err) {
          console.error(err);
          this.loading = false;
        } finally {
          this.loading = false;
        }
      }
    }, 100);

    this.selectedLanguage = this.translate.currentLang || localStorage.getItem('language') || 'fr';
    this.translate.use(this.selectedLanguage);

    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });

    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.resetPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.pattern(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/)]],
      newPasswordConf: ['', [Validators.required]],
    });

    if (this.passwordsNeedsRenewal) {
      this.resetPasswordForm.get('email').disable();
      this.resetPasswordForm.get('password').disable();
    } else {
      this.resetPasswordForm.get('email').clearValidators();
      this.resetPasswordForm.get('password').clearValidators();
      this.resetPasswordForm.get('email').updateValueAndValidity();
      this.resetPasswordForm.get('password').updateValueAndValidity();
    }

    this.resetPasswordForm.get('newPassword')?.valueChanges.subscribe(value => {
      this.checkPasswordStrength(value);
      this.checkPasswordsMatch();
    });

    this.resetPasswordForm.get('newPasswordConf')?.valueChanges.subscribe(() => {
      this.checkPasswordsMatch();
    });

    const url = new URL(location.href);
    const token = url.searchParams.get('token');

    if (token) {
      this.loading = true;
      this.authService.verifyForgotMyPasswordToken({ token }).subscribe({
        next: () => {
          this.forgottenPasswordToken = token;
          this.loading = false;
          this.resetPasswordMode = true;
        },
        error: () => {
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.InvalidPasswordChangeLink'), 'Ok', { duration: 2000 });
        },
      });
    }

    const inviteToken = url.searchParams.get('invite') || this.aroute.snapshot.queryParamMap.get('invite');
    if (inviteToken) {
      this.loading = true;
      this.authService.getInviteInfo(inviteToken).subscribe({
        next: (res) => {
          this.loading = false;
          if (!res.payload.userExists) {
            this.router.navigate(['/signup'], { queryParams: { invite: inviteToken, email: res.payload.email } });
          } else {
            this.inviteInfo = res.payload;
            this.loginForm.patchValue({ email: res.payload.email });
          }
        },
        error: () => {
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Invalid or expired invitation', 'Ok', { duration: 3000 });
        }
      });
    }
  }

  checkPasswordStrength(password: string) {
    if (!password) {
      this.passwordCriteria.forEach(c => c.met = false);
      this.passwordStrength = { score: 0, level: 'weak', label: 'Sessions.Login.PasswordStrength.Weak' };
      return;
    }

    let score = 0;
    
    // Length
    const lengthMet = password.length >= 8;
    this.passwordCriteria.find(c => c.key === 'length')!.met = lengthMet;
    if (lengthMet) score += 1;

    // Uppercase
    const upperMet = /[A-Z]/.test(password) && /[a-z]/.test(password);
    this.passwordCriteria.find(c => c.key === 'uppercase')!.met = upperMet;
    if (upperMet) score += 1;

    // Number
    const numberMet = /[0-9]/.test(password);
    this.passwordCriteria.find(c => c.key === 'number')!.met = numberMet;
    if (numberMet) score += 1;

    // Special
    const specialMet = /[#?!@$%^&*-]/.test(password);
    this.passwordCriteria.find(c => c.key === 'special')!.met = specialMet;
    if (specialMet) score += 1;

    if (score < 2) {
      this.passwordStrength = { score, level: 'weak', label: 'Sessions.Login.PasswordStrength.Weak' };
    } else if (score === 2) {
      this.passwordStrength = { score, level: 'fair', label: 'Sessions.Login.PasswordStrength.Fair' };
    } else if (score === 3) {
      this.passwordStrength = { score, level: 'good', label: 'Sessions.Login.PasswordStrength.Good' };
    } else {
      this.passwordStrength = { score, level: 'strong', label: 'Sessions.Login.PasswordStrength.Strong' };
    }
  }

  checkPasswordsMatch() {
    const newPass = this.resetPasswordForm.get('newPassword')?.value;
    const confirm = this.resetPasswordForm.get('newPasswordConf')?.value;
    
    if (confirm) {
      this.passwordsMatch = newPass === confirm;
    } else {
      this.passwordsMatch = true; // don't show error if confirm is empty
    }
  }

  toggleHide() {
    this.hide = !this.hide;
  }

  signinWithProvider(provider: string): void {
    this.loading = true;
    const returnUrl = this.aroute.snapshot.queryParamMap.get('returnUrl') || '';
    const skipGetUserData = returnUrl.includes('autoJoin=true');

    this.authService
      .loginWithProvider(provider, skipGetUserData)
      .then(() => {
        this.onSuccessNavigation();
      })
      .catch((error) => {
        console.error(provider, error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  onSubmit() {
    this.loading = true;

    if (this.resetPasswordMode) {
      this.authService
        .resetForgottenPassword({
          token: this.forgottenPasswordToken,
          newPassword: this.resetPasswordForm.value.newPassword,
        })
        .subscribe({
          complete: () => {
            this.resetPasswordMode = false;
            this.loading = false;
          },
          error: () => {
            this.loading = false;
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
          },
        });
    } else if (this.isForgotPasswordView) {
      this.authService
        .sendPasswordRecoveryEmail(this.forgotPasswordForm.value.email)
        .subscribe({
          next: () => {
            this.loading = false;
            this.toast.snackbar.open(this.translate.instant('Snackbar.PasswordChangeEmailSendingSuccess'), 'Ok', { duration: 2000 });
            this.isForgotPasswordView = false;
          },
          error: () => {
            this.loading = false;
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
          }
        });
    } else if (this.passwordsNeedsRenewal) {
      if (this.resetPasswordForm.value.newPassword === this.resetPasswordForm.value.newPasswordConf) {
        const form = this.resetPasswordForm.value;
        this.authService.updateUserPassword(form).then(() => {
          this.loading = false;
          this.onSuccessNavigation();
        });
      } else {
        this.loading = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.PasswordConfirmMismatch'), 'Ok', { duration: 2000 });
      }
    } else {
      if (!this.loginForm.invalid) {
        const returnUrl = this.aroute.snapshot.queryParamMap.get('returnUrl') || '';
        const skipGetUserData = returnUrl.includes('autoJoin=true');

        this.authService
          .login(this.loginForm.value, skipGetUserData)
          .then((data) => {
            if (skipGetUserData) {
              this.onSuccessNavigation();
            } else {
              this.handleLoginSuccess(data);
            }
          })
          .catch((error) => {
            this.loading = false;
            this.toast.snackbar.open(this.translate.instant('Snackbar.CredentialsErrors'), 'Ok', { duration: 2000 });
            console.error(error);
          });
      }
    }
  }

  handleLoginSuccess(data): void {
    if (data.tempPassword) {
      this.passwordsNeedsRenewal = true;
      this.loading = false;
    } else {
      this.onSuccessNavigation();
    }
  }

  onForgotMyPasswordEvent() {
    this.isForgotPasswordView = !this.isForgotPasswordView;
  }

  async onSuccessNavigation() {
    this.loading = true;
    const url = new URL(location.href);
    const inviteToken = url.searchParams.get('invite') || this.aroute.snapshot.queryParamMap.get('invite');
    
    if (inviteToken) {
      try {
        await firstValueFrom(this.authService.acceptInvite(inviteToken));
      } catch (err) {
        console.error('Failed to accept invite', err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 3000 });
      }
    } else {
      try {
        await firstValueFrom(this.authService.checkDomainAutoJoin());
      } catch (err) {
        // Silent catch for domain auto-join check
      }
    }
    
    this.loading = false;
    const returnUrl = this.aroute.snapshot.queryParams['returnUrl'] || '/home';
    this.router.navigateByUrl(returnUrl);
  }
}
